import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { todoService } from '../services';
import Select from './Select';
import {
  CheckSquare,
  ChevronDown,
  Plus,
  X,
  Calendar,
  Trash2,
  Pencil,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * HeaderTodo
 * Compact "Organization To-Do" popover that lives in the top navbar.
 * Reuses the same fields & API (todoService) as the full /manager/todos page:
 *   title, description, due_date, priority, status
 * - "+ Add Task" toggles an inline create form
 * - clicking a task (or its pencil) loads it back into the form to rename/edit
 * - "View all" navigates to the full To-Do page
 */

const EMPTY_FORM = {
  title: '',
  description: '',
  due_date: '',
  priority: 'medium',
  status: 'pending',
};

const priorityBadge = (priority) => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
    case 'low':
      return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
    case 'medium':
    default:
      return 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400';
  }
};

const formatDate = (d) => {
  if (!d) return 'No date';
  try {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(new Date(d));
  } catch {
    return 'No date';
  }
};

const HeaderTodo = () => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const pendingCount = todos.filter((t) => t.status !== 'completed').length;
  const doneCount = todos.filter((t) => t.status === 'completed').length;

  // Only preview the first few tasks in the header popover; the full list lives
  // on the "View all" page (/manager/todos).
  const PREVIEW_LIMIT = 4;
  const previewTodos = todos.slice(0, PREVIEW_LIMIT);
  const hiddenCount = todos.length - previewTodos.length;

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const res = await todoService.getAllTodos();
      if (res.success) setTodos(res.data || []);
    } catch (err) {
      // Silent in the header — the full page surfaces errors.
      console.error('Error fetching todos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load once on mount so the badge count is correct before opening.
  useEffect(() => {
    fetchTodos();
  }, []);

  // Close popover on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    if (showForm) {
      resetForm();
      return;
    }
    setEditingId(null);
    setFormData({ ...EMPTY_FORM, due_date: new Date().toISOString().split('T')[0] });
    setShowForm(true);
  };

  const openEditForm = (todo) => {
    setEditingId(todo.id);
    setFormData({
      title: todo.title || '',
      description: todo.description || '',
      due_date: todo.due_date ? todo.due_date.split('T')[0] : '',
      priority: todo.priority || 'medium',
      status: todo.status || 'pending',
    });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    try {
      setSubmitting(true);
      const res = editingId
        ? await todoService.updateTodo(editingId, formData)
        : await todoService.createTodo(formData);
      if (res.success) {
        toast.success(editingId ? 'Task updated' : 'Task created');
        resetForm();
        fetchTodos();
      }
    } catch (err) {
      toast.error(err.message || 'Error saving task');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = async (todo) => {
    const newStatus = todo.status === 'completed' ? 'pending' : 'completed';
    // Optimistic update
    setTodos((prev) => prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)));
    try {
      await todoService.updateTodo(todo.id, { ...todo, status: newStatus });
    } catch (err) {
      toast.error(err.message || 'Error updating status');
      fetchTodos();
    }
  };

  const handleDelete = async (todo) => {
    const prev = todos;
    setTodos((p) => p.filter((t) => t.id !== todo.id));
    try {
      await todoService.deleteTodo(todo.id);
      toast.success('Task deleted');
    } catch (err) {
      toast.error(err.message || 'Error deleting task');
      setTodos(prev);
    }
  };

  const goToFullPage = () => {
    setOpen(false);
    navigate('/manager/todos');
  };

  const inputClass =
    'w-full px-3 py-2 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-[13px] text-gray-800 dark:text-gray-100';

  return (
    <div className="relative" ref={wrapRef}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 h-9 px-2.5 whitespace-nowrap rounded-lg border transition-colors ${
          open
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            : 'bg-white dark:bg-[#31373f] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#3a4048]'
        }`}
        title="To-Do-List"
      >
        <CheckSquare className="w-[18px] h-[18px] text-blue-600 dark:text-blue-400 shrink-0" />
        <span className="hidden lg:inline text-[13px] font-semibold text-gray-700 dark:text-gray-200">To-Do List</span>
        {pendingCount > 0 && (
          <span className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold shrink-0">
            {pendingCount}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[360px] max-w-[calc(100vw-2rem)] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <div className="min-w-0">
              <h3 className="text-[15px] font-bold text-gray-800 dark:text-gray-100">To-Do-List</h3>
              <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-0.5">
                {pendingCount} pending {pendingCount === 1 ? 'task' : 'tasks'}
              </p>
            </div>
            <button
              onClick={openCreateForm}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition shrink-0 ${
                showForm && !editingId
                  ? 'bg-gray-100 dark:bg-[#31373f] text-gray-600 dark:text-gray-300'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {showForm && !editingId ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {showForm && !editingId ? 'Close' : 'Add Task'}
            </button>
          </div>

          {/* Inline create / edit form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c]">
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Task Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Monthly Safety Check"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Additional details..."
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Due Date</label>
                  <input type="date" name="due_date" value={formData.due_date} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Priority</label>
                  <Select
                    value={formData.priority}
                    onChange={(v) => handleChange({ target: { name: 'priority', value: v } })}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
              {editingId && (
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Status</label>
                  <Select
                    value={formData.status}
                    onChange={(v) => handleChange({ target: { name: 'status', value: v } })}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                    className="w-full"
                  />
                </div>
              )}
              <div className="flex gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#31373f] transition font-medium text-[13px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !formData.title.trim()}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium text-[13px]"
                >
                  {submitting ? 'Saving…' : editingId ? 'Update Task' : 'Save Task'}
                </button>
              </div>
            </form>
          )}

          {/* Task list */}
          <div className="max-h-[280px] overflow-y-auto">
            {loading && todos.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-gray-400">Loading…</p>
            ) : todos.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] text-gray-400">No tasks yet.</p>
            ) : (
              previewTodos.map((todo) => {
                const done = todo.status === 'completed';
                return (
                  <div
                    key={todo.id}
                    className="group flex items-center gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-800/60 last:border-b-0 hover:bg-gray-50/70 dark:hover:bg-[#222]"
                  >
                    {/* Toggle complete */}
                    <button
                      onClick={() => toggleStatus(todo)}
                      className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        done
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 text-transparent hover:border-green-500'
                      }`}
                      title={done ? 'Mark as pending' : 'Mark as done'}
                    >
                      <Check className="w-3 h-3" />
                    </button>

                    {/* Content */}
                    <button
                      onClick={() => openEditForm(todo)}
                      className="flex-1 min-w-0 text-left"
                      title="Edit / rename task"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-[13px] font-bold truncate text-gray-800 dark:text-gray-100 ${done ? 'line-through text-gray-400' : ''}`}>
                          {todo.title}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide shrink-0 ${priorityBadge(todo.priority)}`}>
                          {todo.priority}
                        </span>
                      </div>
                      {todo.description && (
                        <p className={`text-[12px] text-gray-400 dark:text-gray-500 truncate ${done ? 'line-through' : ''}`}>
                          {todo.description}
                        </p>
                      )}
                    </button>

                    {/* Due date */}
                    <div className="hidden sm:flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 shrink-0">
                      <Calendar className="w-3 h-3" />
                      {formatDate(todo.due_date)}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        onClick={() => openEditForm(todo)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                        title="Edit / rename"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(todo)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* "more" hint when the list is truncated */}
          {hiddenCount > 0 && (
            <button
              onClick={goToFullPage}
              className="w-full px-4 py-2 text-[12px] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-900/10 border-t border-gray-100 dark:border-gray-800 transition"
            >
              +{hiddenCount} more {hiddenCount === 1 ? 'task' : 'tasks'} — view all
            </button>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c]">
            <div className="flex items-center gap-3 text-[11px] font-medium text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Total: {todos.length}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Pending: {pendingCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Done: {doneCount}
              </span>
            </div>
            <button onClick={goToFullPage} className="text-[12px] font-semibold text-blue-600 dark:text-blue-400 hover:underline">
              View all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderTodo;
